import { useState } from 'react';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { Button } from '@mui/material';
import { DetailViewCard } from '../../../../Compounds/CardWithTitle/DetailViewCard';
import { AuditStatus, AuditType, RunTotalAudit, updateRunTotalAudit } from '../../../../../store/RunTotalAudit';
import { DetailPanelContainer } from '../../../../Compounds/DetailPanel/DetailPanelContainer';
import { BasicDataGrid } from '../../../../Compounds/Table/BasicDataGrid';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { GridCustomColDef } from '../../../../types';
import { runTotalAuditSectionsInfo, runTotalAuditSectionMeta } from './runTotalAuditHelpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../../../utils/styleHelpers';
import { getFormattedDate, numberValueFormatter } from '../../../../../utils/helpers';

export interface RunTotalAuditDetailsParams {
  data: RunTotalAudit;
}

export interface AuditStatsRow {
  id: string;
  tableName: string;
  beforeCount: number | string;
  afterCount: number | string;
  changed: number | string;
  isTotal?: boolean;
}

export const statisticsCols: GridCustomColDef[] = [
  {
    headerName: 'Table',
    flex: 1,
    field: 'tableName',
    type: 'singleSelect',
    sortable: false,
    filterable: false,
    cellClassName: (params: GridCellParams) => {
      return params.row.isTotal ? 'font-semibold' : '';
    }
  },
  {
    headerName: 'Before',
    flex: 1,
    field: 'beforeCount',
    type: 'singleSelect',
    sortable: false,
    filterable: false,
    valueFormatter: (value) => numberValueFormatter(value),
    cellClassName: (params: GridCellParams) => {
      return params.row.isTotal ? 'font-semibold' : '';
    }
  },
  {
    headerName: 'After',
    flex: 1,
    field: 'afterCount',
    type: 'singleSelect',
    sortable: false,
    filterable: false,
    valueFormatter: (value) => numberValueFormatter(value),
    cellClassName: (params: GridCellParams) => {
      return params.row.isTotal ? 'font-semibold' : '';
    }
  },
  {
    headerName: 'Changed',
    flex: 1,
    field: 'changed',
    type: 'singleSelect',
    sortable: false,
    filterable: false,
    valueFormatter: (value) => numberValueFormatter(value),
    cellClassName: (params: GridCellParams) => {
      return params.row.isTotal ? 'font-semibold' : '';
    }
  }
];

export const runTotalAuditStatistics = (
  data: RunTotalAudit
): {
  isError: boolean;
  errorMessage?: string;
  rows?: AuditStatsRow[];
  columns?: typeof statisticsCols;
} => {
  if (data.status === AuditStatus.ERROR) {
    return {
      isError: true,
      errorMessage: data.errorMessage ?? 'An unknown error occurred during the audit.'
    };
  }

  const tableConfigs = [
    {
      name: 'RPM Account',
      before: data.rpmAccountTotalRowsBefore,
      after: data.rpmAccountTotalRowsAfter,
      changed: data.rpmAccountRowsUpdated
    },
    {
      name: 'Asset',
      before: data.assetTotalRowsBefore,
      after: data.assetTotalRowsAfter,
      changed: data.assetRowsUpdated
    },
    {
      name: 'Provisional Asset',
      before: data.provisionalAssetTotalRowsBefore,
      after: data.provisionalAssetTotalRowsAfter,
      changed: data.provisionalAssetRowsUpdated
    },
    {
      name: 'Ticket',
      before: data.ticketTotalRowsBefore,
      after: data.ticketTotalRowsAfter,
      changed: data.ticketRowsUpdated
    },
    {
      name: 'Customer Supplier',
      before: data.customerBySupplierTotalRowsBefore,
      after: data.customerBySupplierTotalRowsAfter,
      changed: data.customerBySupplierRowsUpdated
    },
    {
      name: 'Customer',
      before: data.customerTotalRowsBefore,
      after: data.customerTotalRowsAfter,
      changed: data.customerRowsUpdated
    }
  ];

  const tableRows: AuditStatsRow[] = tableConfigs.map((config, index) => {
    const beforeCount = config.before || 0;
    const afterCount = config.after || 0;
    const changed = config.changed || 0;
    const displayBefore = config.before === null ? '-' : beforeCount;
    const displayAfter = config.after === null ? '-' : afterCount;
    const displayChanged = displayBefore === '-' || displayAfter === '-' ? '-' : changed;
    return {
      id: `row-${index}`,
      tableName: config.name,
      beforeCount: displayBefore,
      afterCount: displayAfter,
      changed: displayChanged
    };
  });

  const totalBefore = data.allTablesTotalRowsBefore || 0;
  const totalAfter = data.allTablesTotalRowsAfter || 0;
  const totalChanged = data.allTablesRowsUpdated || 0;
  const displayTotalBefore = data.allTablesTotalRowsBefore === null ? '-' : totalBefore;
  const displayTotalAfter = data.allTablesTotalRowsAfter === null ? '-' : totalAfter;
  const displayTotalChanged = displayTotalBefore === '-' || displayTotalAfter === '-' ? '-' : totalChanged;
  const totalRow: AuditStatsRow = {
    id: 'row-total',
    tableName: 'Total',
    beforeCount: displayTotalBefore,
    afterCount: displayTotalAfter,
    changed: displayTotalChanged,
    isTotal: true
  };

  const runTotalAuditData = [...tableRows, totalRow];

  return {
    isError: false,
    rows: runTotalAuditData,
    columns: statisticsCols
  };
};

export const RunTotalAuditDetails = ({ data }: RunTotalAuditDetailsParams) => {
  const [loadingStage, setLoadingStage] = useState<'before' | 'after' | null>(null);
  const { isError, errorMessage, rows: runTotalAuditData, columns: statisticsCols } = runTotalAuditStatistics(data);
  const isManual = data.auditType === AuditType.MANUAL;
  const beforeCompleted = !!data.beforeSnapshotTimestamp;
  const afterCompleted = !!data.afterSnapshotTimestamp;

  const handleUpdateStage = async (stage: 'before' | 'after') => {
    setLoadingStage(stage);
    await updateRunTotalAudit(data.id, stage);
    setLoadingStage(null);
  };

  return (
    <div
      className="space-y-4"
      data-testid="run-total-audit-details-drawer">
      <DetailViewCard
        enableCollapse
        key="statistics_detail"
        title="Statistics">
        {isError ? (
          <div className="p-4 text-red font-semibold">Error: {errorMessage}</div>
        ) : (
          <div
            className="w-full"
            data-testid="rpm-mini-table-container">
            <div
              className="w-full"
              data-testid="run-total-audit-mini-table-container">
              <BasicDataGrid
                sxStyle={DEFAULT_MINI_TABLE_SX_STYLES}
                className="flex"
                rows={runTotalAuditData || []}
                columns={statisticsCols || []}
                dataTestId="run-total-audit-mini-data-grid"
              />
            </div>
          </div>
        )}
      </DetailViewCard>
      <div className="rounded-xl border border-grey-2">
        <DetailPanelContainer data={runTotalAuditSectionsInfo(data)} />
        <div className="w-full flex flex-wrap px-6 [&amp;:not(:last-child)]:border-b">
          <div className="w-1/2 flex flex-col mb-1 pr-2 pb-2">
            <p className="font-sans text-grey-6 text-[14px] font-normal">Before</p>
            <div>
              {loadingStage === 'before' ? (
                <div className="flex">
                  <ComponentSpinner sx={{ height: '3rem', width: '3rem' }} />
                </div>
              ) : beforeCompleted ? (
                <p className="font-sans text-grey-9 text-[14px] font-normal">
                  {getFormattedDate(data.beforeSnapshotTimestamp, 'lll')}
                </p>
              ) : isManual ? (
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={() => handleUpdateStage('before')}>
                  Snapshot
                </Button>
              ) : (
                <p className="font-sans text-grey-9 text-[14px] font-normal">-</p>
              )}
            </div>
          </div>
          <div className="w-1/2 flex flex-col mb-1 pr-2 pb-2">
            <p className="font-sans text-grey-6 text-[14px] font-normal">After</p>
            <div>
              {loadingStage === 'after' ? (
                <div className="flex">
                  <ComponentSpinner sx={{ height: '3rem', width: '3rem' }} />
                </div>
              ) : afterCompleted ? (
                <p className="font-sans text-grey-9 text-[14px] font-normal">
                  {getFormattedDate(data.afterSnapshotTimestamp, 'lll')}
                </p>
              ) : isManual && beforeCompleted ? (
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={() => handleUpdateStage('after')}>
                  Snapshot
                </Button>
              ) : (
                <p className="font-sans text-grey-9 text-[14px] font-normal">-</p>
              )}
            </div>
          </div>
        </div>
      </div>
      <DetailViewCard
        enableCollapse
        key="meta_detail"
        title="Meta">
        <div>
          <DetailPanelContainer
            dataTestId="meta_detail-container"
            data={runTotalAuditSectionMeta(data)}
          />
        </div>
      </DetailViewCard>
    </div>
  );
};
